import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BodyText, Button, Div, Flex, Gutter, Icon, Loader, ModalDialog, SupportedIconName, Title } from '@gaiads/telia-react-component-library';
import { GenericTable, PageHeader } from '@cdaas/reusable-components-daas';
import { AdminBundleAcquisitionPrice, BundlePrice, Country, SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '../../../store';
import { selectAllSalesChannels } from '../../../store/salesChannels';
import { setNotification } from '../../../store/ui';
import { addSalesChannelById, removeSalesChannelById } from '../../../store/adminBundles';
import {
  productServiceAdminBundleAcquisitionPricesClient,
  productServiceAdminBundlePricesClient,
  productServiceAdminBundlesClient,
} from '../../../api-client/product-service-client';
import { selectAllCountries } from '../../../store/adminCountries';
import EditBundleSalesChannel from './EditBundleSalesChannel';
import { BundleSalesChannelListItem, EditBundleSalesChannelMode, SalesChannelDrawerProps, SaveStatusOptions, VariantsWithMainInfo } from './types';

import '../../../shared/SalesChannelDrawer/SalesChannelDrawer.scss';

export const getCountryById = (countries: Country[], countryId: number) => countries.find(country => country.id === countryId);
const getExistingSalesChannels = (prices: BundlePrice[], allSalesChannels: SalesChannel[], countries: Country[]): BundleSalesChannelListItem[] => {
  if (!prices || !Array.isArray(prices)) {
    return [];
  }

  return prices.map(price => {
    const salesChannel = allSalesChannels.find(salesChannel => salesChannel.uuid === price.sales_channel_uuid);
    const salesChannelName = salesChannel && salesChannel.name ? salesChannel.name : '';
    const salesChannelCountry = salesChannel && getCountryById(countries, salesChannel.country_id);
    const salesChannelCurrency = salesChannelCountry && salesChannelCountry.currency ? salesChannelCountry.currency : '';
    return {
      ...price,
      name: salesChannelName,
      currency: salesChannelCurrency,
    };
  });
};

// Get sales channels which haven't been added to variant already
const getNotAddedSalesChannels = (variantSalesChannels: BundleSalesChannelListItem[], allSalesChannels: SalesChannel[]) => {
  return allSalesChannels.filter(sc => !variantSalesChannels.map(pSalesChannel => pSalesChannel.sales_channel_uuid).includes(sc.uuid || ''));
};

const getAddedSalesChannels = (variantSalesChannels: BundleSalesChannelListItem[], allSalesChannels: SalesChannel[]) => {
  return allSalesChannels.filter(sc => variantSalesChannels.map(pSalesChannel => pSalesChannel.sales_channel_uuid).includes(sc.uuid || ''));
};

export const SalesChannelDrawer = ({ selectedItems, onCloseClick, defaultSalesChannelUuid, refreshList }: SalesChannelDrawerProps) => {
  const { t: bundlesT } = useTranslation('bundlesview');
  const { t: productsT } = useTranslation('productsscreen');
  const dispatch = useDispatch();
  const allSalesChannels = useSelector((state: RootState) => selectAllSalesChannels(state));
  const countries = useSelector((state: RootState) => selectAllCountries(state));

  // At this point we allow just one bundle item in drawer
  const bundleItem = selectedItems[0];
  const [loading, setLoading] = useState<boolean>(false);
  const [bundleSalesChannels, setBundleSalesChannels] = useState<BundleSalesChannelListItem[]>([]);
  const [bundleVariants, setBundleVariants] = useState<VariantsWithMainInfo[]>([]);
  const [rows, setRows] = useState<{ salesChannelId: number; isOpen: boolean }[]>(
    // Sales channels always have ids as they are fetched through admin endpoint
    allSalesChannels.map(salesChannel => {
      if (defaultSalesChannelUuid && defaultSalesChannelUuid === salesChannel.uuid) {
        return { salesChannelId: salesChannel.id, isOpen: true };
      } else {
        return { salesChannelId: salesChannel.id, isOpen: false };
      }
    }),
  );
  const [confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen] = useState<boolean>(false);
  const [salesChannelToBeRemoved, setSalesChannelToBeRemoved] = useState<BundleSalesChannelListItem>();

  const bundleName = bundleItem && `${bundleItem.brand} ${bundleItem.name}`;

  useEffect(() => {
    const fetchBundleDetail = async () => {
      setLoading(true);
      try {
        const bundleResponse = await productServiceAdminBundlesClient.getAdminBundle(bundleItem.id);
        const bundleContentOverviewResponse = await productServiceAdminBundlesClient.getAdminBundleContentOverview(bundleItem.id);
        const { bundle } = bundleResponse.data;
        const variants = _.uniqBy(
          [...bundleContentOverviewResponse.data.main_item_options, ...bundleContentOverviewResponse.data.items.flatMap(item => item.options)],
          variant => variant.id,
        );
        const combinedSalesChannels = getExistingSalesChannels(bundle.prices || [], allSalesChannels, countries);
        setBundleSalesChannels(combinedSalesChannels);
        const variantsWithMainInfo: VariantsWithMainInfo[] =
          variants?.map(variant => {
            return {
              ...variant,
              isMain: bundle.main_item_option_ids.includes(variant.id),
            };
          }) || [];
        setBundleVariants(variantsWithMainInfo || []);
      } catch {
        dispatch(
          setNotification({
            text: bundlesT('errors.fetchBundleDetails'),
            show: true,
            type: 'error',
          }),
        );
      } finally {
        setLoading(false);
      }
    };
    fetchBundleDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleItem]);

  useEffect(() => {
    const handleEsc = (event: { keyCode: number }) => {
      if (event.keyCode === 27) {
        onCloseClick();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeBundleFromSalesChannel = async () => {
    setConfirmDeleteModalIsOpen(false);
    if (!salesChannelToBeRemoved) {
      return;
    }

    try {
      setLoading(true);
      const { id, sales_channel_uuid } = salesChannelToBeRemoved;
      const response = await productServiceAdminBundlePricesClient.deleteBundlePrice(bundleItem.id, id);
      if (response.status === 204) {
        dispatch(
          removeSalesChannelById({
            salesChannelUUID: sales_channel_uuid || '',
            bundleId: bundleItem.id,
          }),
        );

        if (sales_channel_uuid) {
          const response = await productServiceAdminBundleAcquisitionPricesClient.getAcquisitionPrices(bundleItem.id, sales_channel_uuid);
          const { prices } = response.data;
          const updatedPrices = prices.map(price => ({
            variant_id: price.variant_id,
            default_amount: price.default_amount,
          }));
          await productServiceAdminBundleAcquisitionPricesClient.putAcquisitionPrices(bundleItem.id, sales_channel_uuid || '', {
            prices: updatedPrices,
          });
        }
      }
    } catch (e) {
      console.log(e);
      dispatch(
        setNotification({
          text: bundlesT('errors.deletingSalesChannel'),
          show: true,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromSalesChannel = async (uuid: string | undefined) => {
    if (uuid) {
      const bundlePrice = bundleSalesChannels.find(bundleSalesChannel => bundleSalesChannel.sales_channel_uuid === uuid);
      if (bundlePrice) {
        setSalesChannelToBeRemoved(bundlePrice);
      }
      setConfirmDeleteModalIsOpen(true);
    }
  };

  const closeAccordionBySalesChannelId = (salesChannelId: number) => {
    const updatedRows = rows.map(r => {
      if (r.salesChannelId === salesChannelId) {
        return { ...r, isOpen: false };
      }
      return r;
    });
    setRows(updatedRows);
  };

  // Add new or edit
  const handleSaveClick = async (
    bundlePrice: BundlePrice,
    acquisitionPrices: AdminBundleAcquisitionPrice[],
    mode: EditBundleSalesChannelMode,
    salesChannelId: number,
  ): Promise<SaveStatusOptions> => {
    let returnValue: SaveStatusOptions = SaveStatusOptions.Success;

    try {
      setLoading(true);
      let priceResponse;

      if (mode === EditBundleSalesChannelMode.Add) {
        priceResponse = await productServiceAdminBundlePricesClient.postBundlePrice(bundleItem.id, bundlePrice);
      } else {
        priceResponse = await productServiceAdminBundlePricesClient.putBundlePrice(bundleItem.id, bundlePrice.id, bundlePrice);
      }

      await productServiceAdminBundleAcquisitionPricesClient.putAcquisitionPrices(bundleItem.id, bundlePrice.sales_channel_uuid || '', {
        prices: acquisitionPrices,
      });

      if (priceResponse.status === 200 || priceResponse.status === 201) {
        if (mode === EditBundleSalesChannelMode.Add) {
          dispatch(
            addSalesChannelById({
              salesChannelUUID: bundlePrice.sales_channel_uuid || '',
              bundleId: bundleItem.id,
            }),
          );
        } else {
          const updateBundleSalesChannel = bundleSalesChannels.map(bsc => {
            if (bsc.sales_channel_uuid === bundlePrice.sales_channel_uuid) {
              bsc.amount = bundlePrice.amount;
              bsc.type = bundlePrice.type;
              bsc.vat_percentage = bundlePrice.vat_percentage;
              bsc.insured = bundlePrice.insured;
            }
            return bsc;
          });
          setBundleSalesChannels(updateBundleSalesChannel);
        }
        dispatch(
          setNotification({
            text: bundlesT(mode === EditBundleSalesChannelMode.Add ? 'notification.bundleAddedToChannel' : 'notification.salesChannelEdited'),
            show: true,
            type: 'success',
          }),
        );
        closeAccordionBySalesChannelId(salesChannelId);

        if (refreshList) {
          dispatch(refreshList());
        }
      }
    } catch {
      dispatch(
        setNotification({
          text: bundlesT(mode === EditBundleSalesChannelMode.Add ? 'errors.addingSalesChannel' : 'errors.editingSalesChannel'),
          show: true,
          type: 'error',
        }),
      );
      returnValue = SaveStatusOptions.Error;
    } finally {
      setLoading(false);
    }
    return returnValue;
  };

  const rowAccordionOpen = (row: SalesChannel) => {
    const correctRow = rows.find(r => r.salesChannelId === row.id);
    return correctRow?.isOpen || false;
  };

  const handleRowChevronClick = (row: SalesChannel) => {
    const updatedRows = rows.map(r => {
      if (r.salesChannelId === row.id) {
        return { ...r, isOpen: !r.isOpen };
      }
      return r;
    });
    setRows(updatedRows);
  };

  const handleCancelClick = (salesChannelId: number) => {
    closeAccordionBySalesChannelId(salesChannelId);
  };

  const renderAddedSalesChannelAccordionContent = (salesChannel: SalesChannel) => {
    const countryBySalesChannel = getCountryById(countries, salesChannel.country_id);
    if (!countryBySalesChannel) {
      return <BodyText>{bundlesT('errors.countryNotFound')}</BodyText>;
    }
    const bundleListItemByUUID = bundleSalesChannels.find(bundleItem => bundleItem.sales_channel_uuid === salesChannel.uuid);
    return (
      <EditBundleSalesChannel
        bundleId={bundleItem.id}
        salesChannel={salesChannel}
        bundlePrice={bundleListItemByUUID}
        bundleVariants={bundleVariants}
        country={countryBySalesChannel}
        onCancelClick={handleCancelClick}
        onSaveClick={handleSaveClick}
        mode={EditBundleSalesChannelMode.Edit}
      />
    );
  };
  const formatBundlePriceAmount = (uuid: string) => {
    const bundleSalesChannel = bundleSalesChannels.find(bundleItem => bundleItem.sales_channel_uuid === uuid);
    return `${bundleSalesChannel?.amount} ${bundleSalesChannel?.currency}/${bundlesT('editSalesChannels.month')}`;
  };

  const renderNotAddedSalesChannelAccordionContent = (salesChannel: SalesChannel) => {
    const countryBySalesChannel = getCountryById(countries, salesChannel.country_id);
    if (!countryBySalesChannel) {
      return <BodyText>{bundlesT('errors.countryNotFound')}</BodyText>;
    }
    return (
      <EditBundleSalesChannel
        bundleId={bundleItem.id}
        salesChannel={salesChannel}
        bundleVariants={bundleVariants}
        country={countryBySalesChannel}
        onCancelClick={handleCancelClick}
        onSaveClick={handleSaveClick}
        mode={EditBundleSalesChannelMode.Add}
      />
    );
  };

  const notAddedSalesChannels = getNotAddedSalesChannels(bundleSalesChannels, allSalesChannels);
  const addedSalesChannels = getAddedSalesChannels(bundleSalesChannels, allSalesChannels);

  return (
    <>
      <Div className="sales-channel-drawer__overlay" onClick={() => onCloseClick()}></Div>
      <Div className={`sales-channel-drawer`} backgroundColor={'white'}>
        <Flex className={'sales-channel-drawer__inner-content'} directionColumn occupyVerticalSpace={false}>
          <Flex flexItem padding={{ top: 'md', left: 'md', right: 'md', bottom: 'xxxs' }}>
            <Flex centeredVertically occupyHorizontalSpace alignBaseline>
              <Flex flexItem style={{ flexGrow: 3 }}>
                <PageHeader headingSize={'sm'}>{productsT('sales_channel_drawer.title')}</PageHeader>
              </Flex>
              <Flex flexItem alignRight>
                <Button variant={'text'} onClick={() => onCloseClick()}>
                  <Icon name={'close' as SupportedIconName} clickableAppearance={true} size={'md'} />
                </Button>
              </Flex>
            </Flex>
          </Flex>
          <Flex flexItem padding={{ top: 'md', left: 'md', right: 'md', bottom: 'xxs' }} directionColumn>
            <Title color="gray500" size="xs">
              {bundleName}
            </Title>
          </Flex>
          {loading && (
            <Flex flexItem padding={{ size: 'md' }} directionColumn>
              <Div data-testid={'loading'} alignTextToCenter padding={{ top: 'xxlg' }}>
                <Loader flexItem size="lg" />
              </Div>
            </Flex>
          )}
          {!loading && (
            <Flex flexItem padding={{ top: 'xxs', left: 'md', right: 'md', bottom: 'md' }} directionColumn>
              <div className="sales-channel-drawer__scroll">
                {addedSalesChannels && (
                  <>
                    <BodyText color="gray500">{bundlesT('editSalesChannels.currentlyAdded')}</BodyText>
                    <Gutter size="sm" />
                  </>
                )}
                <GenericTable
                  externalOverwriteAccordionOpen={rowAccordionOpen}
                  loading="none"
                  inputData={addedSalesChannels}
                  noDataText={bundlesT('notification.noAddedSalesChannels')}
                  rowConfigs={[
                    { key: 'name', formatValue: (value: string) => value },
                    {
                      key: 'uuid',
                      formatValue: formatBundlePriceAmount,
                    },
                  ]}
                  isGenericTableInsideCard={true}
                  chevronConfig={{
                    accordionContent: renderAddedSalesChannelAccordionContent,
                    onClickCallback: handleRowChevronClick,
                  }}
                  buttonConfig={{
                    buttonClickCallback: row => handleRemoveFromSalesChannel(row.uuid),
                    buttonProps: { variant: 'secondaryDestructive' },
                    hideAtBreakpoint: undefined,
                    name: bundlesT('editSalesChannels.removeFromChannel'),
                  }}
                />
                <Gutter />
                {notAddedSalesChannels.length > 0 && (
                  <>
                    <BodyText color="gray500">{bundlesT('editSalesChannels.notAdded')}</BodyText>
                    <Gutter size="sm" />
                  </>
                )}

                <GenericTable
                  externalOverwriteAccordionOpen={rowAccordionOpen}
                  loading="none"
                  inputData={notAddedSalesChannels}
                  noDataText={bundlesT('errors.addingSalesChannel')}
                  rowConfigs={[{ key: 'name', formatValue: (value: string) => value }]}
                  isGenericTableInsideCard={true}
                  chevronConfig={{
                    accordionContent: renderNotAddedSalesChannelAccordionContent,
                    onClickCallback: handleRowChevronClick,
                  }}
                />
              </div>
            </Flex>
          )}
        </Flex>
        <ModalDialog
          title={bundlesT('notification.removeFromChannel')}
          closeButtonText={bundlesT('delete_bundle_confirmation.buttons.cancel')}
          acceptButtonText={bundlesT('delete_bundle_confirmation.buttons.accept')}
          isOpen={confirmDeleteModalIsOpen}
          onCloseClick={() => setConfirmDeleteModalIsOpen(false)}
          onAcceptClick={() => removeBundleFromSalesChannel()}
        >
          <BodyText>
            {bundlesT('notification.removeFromChannelLong', {
              bundleName: bundleName,
              salesChannelName: salesChannelToBeRemoved?.name || '',
              interpolation: { escapeValue: false },
            })}
          </BodyText>
        </ModalDialog>
      </Div>
    </>
  );
};

export default SalesChannelDrawer;
